import { useState } from 'react';
import { createHome, updateHomeName, inviteToHome, revokeInvite, removeMember, switchHome } from '../utils/api';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';

const Homes = ({ homes, user, onChanged }) => {
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState(null); // { token, joinUrl }
  const [inviting, setInviting] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createHome({ name: newName.trim() });
      setNewName('');
      onChanged();
    } finally {
      setCreating(false);
    }
  };

  const handleSwitch = async (id) => {
    await switchHome(id);
    onChanged();
    window.location.href = '/';
  };

  const handleRename = async (id) => {
    if (!renameName.trim()) return;
    await updateHomeName(id, { name: renameName.trim() });
    setRenameId(null);
    setRenameName('');
    onChanged();
  };

  const handleInvite = async (homeId) => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const { data } = await inviteToHome(homeId, inviteEmail.trim());
      setInviteResult(data);
      setInviteEmail('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Invite failed', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (homeId, token) => {
    await revokeInvite(homeId, token);
    onChanged();
  };

  const handleRemoveMember = async (homeId, memberId) => {
    if (!(await confirm({ title: 'Remove member', message: 'This person will lose access to this property.', confirmLabel: 'Remove' }))) return;
    try {
      await removeMember(homeId, memberId);
      onChanged();
      showToast('Member removed', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove member', 'error');
    }
  };

  const myUserId = user?._id?.toString();

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Properties</h1>
          <p className="page-subtitle">Manage your homes and invite family members</p>
        </div>
      </div>

      <div style={{ maxWidth: 680 }}>
        {homes.map((home) => {
          const myMember = home.members?.find((m) => (m.user?._id || m.user)?.toString() === myUserId);
          const isOwner = myMember?.role === 'owner';
          const isActive = home._id?.toString() === user?.activeHomeId?.toString();
          const isExpanded = expandedId === home._id;
          const pendingInvites = home.invitations?.filter((inv) => !inv.acceptedAt) || [];

          return (
            <div key={home._id} className="card" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  {renameId === home._id ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        className="form-input"
                        value={renameName}
                        onChange={(e) => setRenameName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleRename(home._id)}
                        autoFocus
                        style={{ flex: 1 }}
                      />
                      <button className="btn btn-primary btn-sm" onClick={() => handleRename(home._id)}>Save</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setRenameId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>{home.name}</span>
                      {isActive && <span className="badge badge-priority-low" style={{ fontSize: '0.7rem' }}>Active</span>}
                      <span className="badge" style={{ fontSize: '0.7rem' }}>{isOwner ? 'Owner' : 'Member'}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                  {!isActive && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleSwitch(home._id)}>
                      Switch
                    </button>
                  )}
                  {isOwner && renameId !== home._id && (
                    <button className="btn btn-secondary btn-sm" onClick={() => { setRenameId(home._id); setRenameName(home.name); }}>
                      Rename
                    </button>
                  )}
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => { setExpandedId(isExpanded ? null : home._id); setInviteResult(null); setInviteEmail(''); }}
                  >
                    {isExpanded ? 'Hide' : 'Members'}
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                  <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Members</p>
                  {home.members?.map((m) => {
                    const memberId = (m.user?._id || m.user)?.toString();
                    const isSelf = memberId === myUserId;
                    const displayName = isSelf
                      ? `${m.user?.email || user.email} (you)`
                      : m.user?.name || m.user?.email || memberId;
                    return (
                      <div key={memberId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                        <span style={{ flex: 1, fontSize: '0.875rem' }}>
                          {displayName}
                          {' '}<span className="badge" style={{ fontSize: '0.7rem' }}>{m.role}</span>
                        </span>
                        {(isOwner || isSelf) && !isSelf && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleRemoveMember(home._id, memberId)}>
                            Remove
                          </button>
                        )}
                        {isSelf && !isOwner && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleRemoveMember(home._id, memberId)}>
                            Leave
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {isOwner && (
                    <>
                      {pendingInvites.length > 0 && (
                        <>
                          <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', margin: '1rem 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending Invitations</p>
                          {pendingInvites.map((inv) => (
                            <div key={inv.token} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                              <span style={{ flex: 1, fontSize: '0.875rem', color: 'var(--muted)' }}>{inv.email}</span>
                              <button className="btn btn-secondary btn-sm" onClick={() => handleRevoke(home._id, inv.token)}>
                                Revoke
                              </button>
                            </div>
                          ))}
                        </>
                      )}

                      <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', margin: '1rem 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Invite by Email</p>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          className="form-input"
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleInvite(home._id)}
                          placeholder="email@example.com"
                          style={{ flex: 1 }}
                          disabled={inviting}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleInvite(home._id)}
                          disabled={!inviteEmail.trim() || inviting}
                        >
                          {inviting ? '…' : 'Invite'}
                        </button>
                      </div>

                      {inviteResult && (
                        <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
                          <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.35rem' }}>Share this link with the person you invited:</p>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <code style={{ flex: 1, fontSize: '0.75rem', wordBreak: 'break-all', color: 'var(--accent)' }}>{inviteResult.joinUrl}</code>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => navigator.clipboard.writeText(inviteResult.joinUrl)}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem' }}>Add Another Property</h3>
          <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              className="form-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Lake Cabin or Rental on Oak St"
              style={{ flex: 1 }}
              disabled={creating}
            />
            <button className="btn btn-primary" type="submit" disabled={!newName.trim() || creating}>
              {creating ? 'Creating…' : 'Add'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Homes;
